import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { MessageCircle } from "lucide-react";

const FAQSection = () => {
  const faqs = [
    {
      question: "How do I register for the event?",
      answer: "You can register by clicking the 'Register Now' button on our website. Select your preferred event (Chennai or Hyderabad), choose your race category (3K, 5K, or 10K), fill in your details, and complete the payment. You'll receive a confirmation email with your registration details and BIB collection information."
    },
    {
      question: "What are the race categories and distances available?",
      answer: "We offer three race categories: 3K Run (₹799) - perfect for beginners and families, 5K Run (₹999) - ideal for fitness enthusiasts, and 10K Run (₹1,199) - for serious runners seeking a challenge. All categories include a BIB, event t-shirt, finisher medal, goodie bag, and e-certificate."
    },
    {
      question: "Where and when can I collect my BIB kit?",
      answer: "BIB kits must be collected the day before the event at the designated collection center. The venue, date, and timing will be communicated via email and SMS closer to the event date. Please bring a valid ID proof for verification. Note: BIB collection on race day is NOT available."
    },
    {
      question: "What does the registration fee include?",
      answer: "Your registration includes: Official Race BIB, Event T-Shirt, Finisher Medal, Goodie Bag with sponsor products, E-Certificate, Refreshments at hydration stations, Post-run nutrition, and Medical support throughout the event."
    },
    {
      question: "Is there an age limit for participation?",
      answer: "For the 3K run, participants of all ages are welcome (children under 12 must be accompanied by an adult). The 5K run is open to participants aged 12 and above. The 10K run requires participants to be at least 16 years old. Medical clearance is recommended for all participants."
    },
    {
      question: "What happens if the event is canceled or postponed?",
      answer: "In case of cancellation or postponement due to unforeseen circumstances (weather, safety concerns, etc.), participants will be notified via email and our social media channels. Registrations will either be transferred to the new date or refunded as per our cancellation policy."
    },
    {
      question: "Will there be medical assistance during the event?",
      answer: "Yes, medical stations and trained professionals will be available at multiple locations along the race course and at the finish line. Ambulances will be on standby for emergencies. Please mention any medical conditions during registration."
    },
    {
      question: "How do I track my race results?",
      answer: "Race results will be available on our website within 24 hours after the event. You can search your results using your BIB number or name. Certificates with your timing can be downloaded from the Results page."
    },
    {
      question: "Can I transfer my registration to someone else?",
      answer: "Registration transfers are allowed up to 7 days before the event date. A transfer fee may apply. Please contact us at info@thefinrun.com with both participant details to process the transfer."
    },
    {
      question: "How can my company become a sponsor?",
      answer: "We offer various sponsorship tiers from Title Sponsor to category-specific partnerships. Visit our Sponsor page or contact our partnership team at info@thefinrun.com or call +91 9618474761 for detailed sponsorship packages and benefits."
    }
  ];

  return (
    <section id="faq" className="section-spacing bg-white" data-testid="faq-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Left Side - Header */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-32">
              <p className="text-[#FF3B30] text-sm uppercase tracking-[0.2em] font-bold mb-4">
                FAQs
              </p>
              <h2 className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] font-['Outfit'] mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-[#4B5563] mb-8">
                Find answers to the most commonly asked questions about our running event. 
                Can't find what you're looking for? Reach out to us directly.
              </p>
              
              {/* Contact Card */}
              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#007AFF]/10 rounded-xl flex items-center justify-center mb-4">
                  <MessageCircle className="w-6 h-6 text-[#007AFF]" />
                </div>
                <h3 className="font-bold text-[#0A0A0A] font-['Outfit'] mb-2">
                  Still have questions?
                </h3>
                <p className="text-[#4B5563] text-sm mb-4">
                  Our team is here to help you with any queries.
                </p>
                <Link to="/#contact">
                  <Button 
                    variant="outline"
                    className="w-full rounded-xl border-[#007AFF] text-[#007AFF] hover:bg-[#007AFF] hover:text-white"
                    data-testid="faq-contact-btn"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Right Side - Accordion */}
          <div className="lg:col-span-3">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem 
                  key={faq.question} 
                  value={faq.question}
                  className="bg-[#FAFAFA] rounded-2xl px-6 border-none"
                  data-testid={`faq-item-${faq.question.slice(0, 20)}`}
                >
                  <AccordionTrigger className="text-left font-semibold text-[#0A0A0A] hover:text-[#FF3B30] hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4B5563] pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
